import React from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { styled, alpha } from '@mui/material/styles';
import { TableData } from './type';
import { useSelector } from 'react-redux';
import { RootStateType } from '@Store';
import EditIcon from '@mui/icons-material/Edit';

const Container = styled(Box)(({ theme }) => ({
  maxHeight: '38rem',
  height: 'fit-content',
  overflow: 'auto',
  borderRadius: 20,
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  background: alpha(theme.palette.background.paper, 0.7),
  backdropFilter: 'blur(12px)',
}));

const HeaderRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  padding: '16px 20px',
  background: alpha(theme.palette.primary.main, 0.04),
  borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
}));

const Row = styled(Box)(({ theme }) => ({
  display: 'flex',
  padding: '14px 20px',
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
  alignItems: 'center',
  transition: '0.2s',

  '&:hover': {
    background: alpha(theme.palette.primary.main, 0.03),
  },
}));

const Cell = styled(Box)<{ flex?: number }>(({ flex = 1 }) => ({
  flex,
  display: 'flex',
  alignItems: 'center',
}));

const EditButton = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  padding: 6,
  borderRadius: 8,

  '&:hover': {
    background: alpha(theme.palette.primary.main, 0.1),
  },
}));

function Table({
  header,
  data,
  EditPermission,
}: {
  header: string;
  data?: TableData[] | [];
  EditPermission: boolean | undefined;
}) {
  const { token } = useSelector((state: RootStateType) => state.loginUser);

  return (
    <Grid container item className="mt-3" sx={{ flexDirection: 'column' }}>
      {/* Title */}
      <Typography sx={{ fontSize: 20, fontWeight: 700, mb: 2 }}>{header}</Typography>

      {/* Container */}
      <Container>
        {/* Header */}
        <HeaderRow>
          <Cell flex={0.5}>
            <Typography fontWeight={700}>Sr.No</Typography>
          </Cell>

          <Cell flex={1.5}>
            <Typography fontWeight={700}>Name</Typography>
          </Cell>

          <Cell flex={2}>
            <Typography fontWeight={700}>Template Details</Typography>
          </Cell>

          <Cell flex={0.5}>
            <Typography fontWeight={700}>Action</Typography>
          </Cell>
        </HeaderRow>

        {/* Rows */}
        {data
          ?.filter((item: { code: string }) => {
            if (header === 'Saved Template') {
              return item.code !== token?.accessGroup;
            }
            return true;
          })
          .map((row, index) => (
            <SingleRow
              key={row.code}
              data={row}
              index={index}
              EditPermission={
                EditPermission
                  ? header === 'Default Template'
                    ? token?.accessGroup === row?.code || token?.accessGroup === 'pa_all'
                    : true
                  : EditPermission
              }
            />
          ))}
      </Container>
    </Grid>
  );
}

function SingleRow({
  data,
  index,
  EditPermission,
}: {
  data: TableData;
  index: number;
  EditPermission: boolean | undefined;
}) {
  const navigate = useNavigate();

  const handleEdit = (code: string) => {
    navigate('edit', { state: { code } });
  };

  return (
    <Row>
      {/* Sr No */}
      <Cell flex={0.5}>
        <Typography>{index + 1}</Typography>
      </Cell>

      {/* Name */}
      <Cell flex={1.5}>
        <Typography fontWeight={500}>{data?.name}</Typography>
      </Cell>

      {/* Details */}
      <Cell flex={2}>
        <Typography color="text.secondary">Created by : {data?.createdBy}</Typography>
      </Cell>

      {/* Action */}
      <Cell flex={0.5}>
        {EditPermission && (
          <EditButton onClick={() => handleEdit(data?.code)}>
            <EditIcon fontSize="small" />
          </EditButton>
        )}
      </Cell>
    </Row>
  );
}

export default Table;
